import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Calendar, Copy, RefreshCw, Power, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { generateToken } from "@/lib/crypto";
import { sha256Hex } from "@/lib/crypto";

export function CalendarFeedSection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showRegenDialog, setShowRegenDialog] = useState(false);

  const { data: feed, isLoading } = useQuery({
    queryKey: ["calendar-feed", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("calendar_feeds" as any)
        .select("*")
        .eq("user_id", user!.id)
        .eq("enabled", true)
        .is("revoked_at", null)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown) as { id: string; token_plain: string; enabled: boolean } | null;
    },
    enabled: !!user?.id,
  });

  const feedUrl = feed
    ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-feed?token=${feed.token_plain}`
    : null;

  const enableMutation = useMutation({
    mutationFn: async () => {
      const token = generateToken(32);
      const hash = await sha256Hex(token);
      const { error } = await supabase.from("calendar_feeds" as any).insert({
        user_id: user!.id,
        token_plain: token,
        token_hash: hash,
        enabled: true,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-feed"] });
      toast.success("Kalenderfeed aktivert");
    },
    onError: () => toast.error("Kunne ikke aktivere kalenderfeed"),
  });

  const disableMutation = useMutation({
    mutationFn: async () => {
      if (!feed) return;
      const { error } = await supabase
        .from("calendar_feeds" as any)
        .update({ enabled: false, revoked_at: new Date().toISOString() })
        .eq("id", feed.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-feed"] });
      toast.success("Kalenderfeed deaktivert");
    },
    onError: () => toast.error("Kunne ikke deaktivere kalenderfeed"),
  });

  const regenerateMutation = useMutation({
    mutationFn: async () => {
      if (!feed) return;
      const token = generateToken(32);
      const hash = await sha256Hex(token);
      // Revoke old
      await supabase
        .from("calendar_feeds" as any)
        .update({ enabled: false, revoked_at: new Date().toISOString() })
        .eq("id", feed.id);
      // Create new
      const { error } = await supabase.from("calendar_feeds" as any).insert({
        user_id: user!.id,
        token_plain: token,
        token_hash: hash,
        enabled: true,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-feed"] });
      setShowRegenDialog(false);
      toast.success("Ny lenke opprettet");
    },
    onError: () => toast.error("Kunne ikke regenerere lenke"),
  });

  const copyUrl = async () => {
    if (!feedUrl) return;
    try {
      await navigator.clipboard.writeText(feedUrl);
      toast.success("Lenke kopiert");
    } catch {
      toast.error("Kunne ikke kopiere");
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            Kalenderfeed
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 p-4 pt-0">
          <p className="text-xs text-muted-foreground">
            Abonner på en privat kalender med aktiviteter for barna dine i Google Calendar, Outlook eller Apple Kalender.
          </p>

          {feed && feedUrl ? (
            /* ── Active state ── */
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  Aktiv
                </Badge>
              </div>

              <div className="flex gap-2">
                <Input
                  readOnly
                  value={feedUrl}
                  className="text-xs font-mono bg-muted/50 truncate"
                  onFocus={(e) => e.target.select()}
                />
                <Button variant="outline" size="icon" onClick={copyUrl} title="Kopier lenke">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>

              <div className="rounded-lg bg-destructive/5 border border-destructive/20 p-3">
                <p className="text-xs text-destructive">
                  Alle som har denne lenken kan se kalenderen. Regenerer lenken hvis du vil sperre gammel tilgang.
                </p>
              </div>

              {/* Onboarding hints */}
              <div className="rounded-lg bg-muted/50 p-3 space-y-1.5">
                <p className="text-xs font-medium text-foreground flex items-center gap-1.5">
                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                  Slik bruker du lenken
                </p>
                <ul className="text-xs text-muted-foreground space-y-1 pl-5 list-disc">
                  <li><strong>Google Calendar:</strong> Innstillinger → Legg til kalender → Fra URL</li>
                  <li><strong>Apple Kalender:</strong> Arkiv → Nytt kalenderabonnement</li>
                  <li><strong>Outlook:</strong> Legg til kalender → Abonner fra nett</li>
                </ul>
                <p className="text-xs text-muted-foreground pt-1 italic">
                  Endringer vises ikke alltid med én gang, fordi kalenderapper oppdaterer abonnementer periodisk.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => setShowRegenDialog(true)}
                  disabled={regenerateMutation.isPending}
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  Regenerer lenke
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  onClick={() => disableMutation.mutate()}
                  disabled={disableMutation.isPending}
                >
                  <Power className="h-3.5 w-3.5 mr-1.5" />
                  Slå av
                </Button>
              </div>
            </div>
          ) : (
            /* ── Disabled state ── */
            <Button
              onClick={() => enableMutation.mutate()}
              disabled={enableMutation.isPending}
              className="w-full"
            >
              {enableMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Calendar className="h-4 w-4 mr-2" />
              )}
              Slå på kalenderfeed
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Regenerate confirmation dialog */}
      <AlertDialog open={showRegenDialog} onOpenChange={setShowRegenDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerer lenke?</AlertDialogTitle>
            <AlertDialogDescription>
              Når du regenererer lenken, slutter den gamle å virke. Du må deretter oppdatere kalenderabonnementene dine med den nye lenken.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => regenerateMutation.mutate()}
              disabled={regenerateMutation.isPending}
            >
              {regenerateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Regenerer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
